import {
  addDays,
  differenceInDays,
  eachMonthOfInterval,
  format,
  getDay,
  getDayOfYear,
  getDaysInMonth,
  isAfter,
  isFriday,
  isMonday
} from 'date-fns';
import { parse } from 'date-fns/esm';
import html2canvas from 'html2canvas';
import Jspdf from 'jspdf';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { StringParam, useQueryParam } from 'use-query-params';

import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Menu,
  MenuItem,
  Slider
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import { useGetQualityRoadmap } from './QualityRoadmap.hooks';

const workstreamColumnWidth = 140;
const defaultDayWidth = 12;
const dayHeight = 40;
const roadmapGrid = { x: 0, y: 0 };

function adjustColor(color: string, amount: number) {
  return (
    '#' +
    color
      .replace(/^#/, '')
      .replace(/../g, color =>
        (
          '0' +
          Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)
        ).substr(-2)
      )
  );
}

const StyledRoadmap = styled.div<{ dayWidth: number }>`
  ${({ dayWidth }) => css`
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: hidden;
    .toolbar {
      padding: 24px;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      .button-container {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: flex-end;
      }
      .today-marker-checkbox {
        margin-right: 20px;
      }
      .scale-slider-label {
        margin-right: 20px;
      }
      .scale-slider {
        margin-left: 10px;
      }
    }
    .roadmap-scroll {
      display: flex;
      flex-direction: column;
      overflow: auto;
      flex: 1;
    }
    .roadmap-container {
      position: relative;
    }
    .timeline {
      position: sticky;
      top: 0;
      z-index: 3;
      display: flex;
      font-weight: bold;
      .epic-name {
        background: #122140;
        color: #fff;
      }
      .month {
        background: #122140;
        color: #fff;
        padding: 10px 0;
        border: solid 1px #8a97b4;
        border-right: none;
        text-align: center;
        &:last-child {
          border-right: solid 1px #8a97b4;
        }
      }
    }
    .epic-name {
      padding: 10px;
      min-width: ${workstreamColumnWidth}px;
      max-width: ${workstreamColumnWidth}px;
      position: sticky;
      left: 0;
      z-index: 2;
      font-weight: bold;
    }
    .epic {
      display: flex;
      border-bottom: solid 2px #999;
      box-sizing: border-box;
      .block {
        display: flex;
        flex-direction: column;
        justify-content: center;
        box-sizing: border-box;
        height: ${dayHeight}px;
        margin: -1px 0 0 0;
        padding: 0 5px;
        border: solid 1px rgba(0, 0, 0, 0.5);
        border-width: 1px 0 1px 1px;
        background: #f4f4f4;
        &:first-child {
          border-left-width: 1px;
        }
        &:hover {
          background: #eee;
          opacity: 1;
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.25);
          z-index: 1;
        }
      }
      .gap,
      .end {
        //border-left: solid 1px rgba(0, 0, 0, 0.5);
        margin-top: -1px;
      }
      .new-line {
        flex-basis: 100%;
      }
      .epic-name {
        background: #fff;
        min-height: 100%;
        border-right: solid 1px #999;
      }
      .requirements-container {
        display: flex;
        width: 100%;
        flex-wrap: wrap;
        background-color: #fff;
        background-size: ${dayWidth * 7}px ${dayHeight * 7}px,
          ${dayWidth * 7}px ${dayHeight * 7}px, ${dayWidth}px ${dayHeight}px,
          ${dayWidth}px ${dayHeight}px;
        background-image: linear-gradient(
            90deg,
            rgba(215, 215, 215, 0.25) ${dayWidth}px,
            transparent 1px
          ),
          linear-gradient(
            90deg,
            rgba(215, 215, 215, 0.25) ${dayWidth}px,
            transparent 1px
          ),
          linear-gradient(rgba(215, 215, 215, 0.5) 1px, transparent 1px),
          linear-gradient(90deg, rgba(215, 215, 215, 0.5) 1px, transparent 1px);
      }
      .requirement-name {
        /* display: -webkit-box; */
        -webkit-box-orient: vertical;
        line-clamp: 2;
        -webkit-line-clamp: 2;
        font-size: 12px;
        font-weight: bold;
        overflow: hidden;
      }
    }
    .today-marker {
      position: absolute;
      top: 38px;
      bottom: 0;
      left: 0;
      z-index: 1;
      pointer-events: none;
      &::before {
        position: relative;
        content: 'Today';
        background: #f6c34b;
        padding: 3px 3px;
        border-radius: 3px;
        font-size: 10px;
        font-weight: bold;
      }
      &::after {
        position: absolute;
        top: 15px;
        left: 50%;
        content: '';
        border: solid 1px #f6c34b;
        height: 100%;
      }
    }
  `}
`;

const QualityRoadmapPage: React.FC = () => {
  const roadmapDivRef = useRef<HTMLDivElement>(null);
  const [exportMenuAncor, setExportMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [showTodayMarker, setShowTodayMarker] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [dayWidth, setDayWidth] = useState(defaultDayWidth);
  const epics = useGetQualityRoadmap();
  console.log('epics', epics);
  // useEffect(() => getRoadmap({ projectKey: project }), [getRoadmap, project]);
  const startTimeline = addDays(new Date(), -30);
  const endTimeline = useMemo(() => {
    // get last month to display on timeline based on roadmap events
    if (!epics) {
      return addDays(new Date(), 360);
    }
    return epics.reduce(
      (result, epic) =>
        epic.requirements
          ? epic.requirements.reduce((result, requirement) => {
              const date = parse(requirement.endDate, 'yyyy-MM-dd', new Date());
              if (isAfter(date, result)) {
                return date;
              }
              return result;
            }, result)
          : result,
      startTimeline
    );
  }, [epics, startTimeline]);

  const getRoadmapCanvas = async () => {
    // generate HTML canvas based on roadmap element container
    if (!roadmapDivRef.current) {
      throw Error('element not initialized');
    }
    const element = roadmapDivRef.current;
    element
      .querySelectorAll<HTMLDivElement>('.timeline')
      .forEach(element => (element.style.position = 'static'));
    element
      .querySelectorAll<HTMLDivElement>('.epic-name')
      .forEach(element => (element.style.position = 'static'));
    const canvas = await html2canvas(element);
    element
      .querySelectorAll<HTMLDivElement>('.timeline')
      .forEach(element => (element.style.position = 'sticky'));
    element
      .querySelectorAll<HTMLDivElement>('.epic-name')
      .forEach(element => (element.style.position = 'sticky'));
    return canvas;
  };
  const getGridPosition = (dayOffset: number) => {
    // update the background grid position based on start month to accurately
    // show weekend markers
    return `
    ${roadmapGrid.x + dayOffset * dayWidth}px ${roadmapGrid.y - 3}px,
    ${roadmapGrid.x + dayWidth * 6 + dayOffset * dayWidth}px ${
      roadmapGrid.y + dayHeight * 6 - 3
    }px,
    ${roadmapGrid.x}px ${roadmapGrid.y - 3}px,
    ${roadmapGrid.x}px ${roadmapGrid.y - 3}px`;
  };
  const months = eachMonthOfInterval({
    start: startTimeline,
    end: endTimeline
  });
  const today = getDayOfYear(new Date());
  //getDayOfYear(new Date(2020, 8, 15)) - getDayOfYear(startTimeline) - 1;
  const dayOfWeek = getDay(startTimeline);
  const weekendOffset = 7 - dayOfWeek;

  const roadmapWidth = months.reduce((result, date) => {
    const days = getDaysInMonth(date);
    return result + days * dayWidth;
  }, workstreamColumnWidth);
  return (
    <StyledRoadmap className='roadmap' dayWidth={dayWidth}>
      {/* <Helmet title='Roadmap' /> */}
      <div className='toolbar'>
        {/* <div className='filters'>
          <ProjectSelectField
            include={['RR', 'RP']}
            value={project}
            onChange={value => setProject(value as string)}
          />
        </div> */}
        <div className='button-container'>
          <FormControlLabel
            className='today-marker-checkbox'
            label='Show Today Marker'
            labelPlacement='start'
            control={
              <Checkbox
                checked={showTodayMarker}
                onChange={() => setShowTodayMarker(!showTodayMarker)}
                color='primary'
              />
            }
          />
          <FormControlLabel
            className='scale-slider-label'
            label='Scale'
            labelPlacement='start'
            control={
              <Slider
                className='scale-slider'
                min={9}
                max={14}
                step={0.02}
                style={{ width: 100 }}
                value={dayWidth}
                onChange={(event, value) => setDayWidth(value as number)}
              />
            }
          />

          <Button
            variant='contained'
            color='primary'
            endIcon={
              isExporting ? (
                <CircularProgress
                  size={20}
                  className='export-progress'
                  color='secondary'
                />
              ) : (
                <ArrowDropDownIcon />
              )
            }
            onClick={e => setExportMenuAnchor(e.currentTarget)}
          >
            Export
          </Button>
          <Menu
            anchorEl={exportMenuAncor}
            keepMounted
            open={Boolean(exportMenuAncor)}
            onClose={e => setExportMenuAnchor(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <MenuItem
              disabled={isExporting}
              onClick={async () => {
                setIsExporting(true);
                const canvas = await getRoadmapCanvas();
                const imgData = canvas.toDataURL('image/png');
                const link = document.createElement('a');
                link.download = 'roadmap.png';
                link.href = imgData;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setIsExporting(false);
              }}
            >
              Image (PNG)
            </MenuItem>
            <MenuItem
              disabled={isExporting}
              onClick={async () => {
                setIsExporting(true);
                const canvas = await getRoadmapCanvas();
                const imgData = canvas.toDataURL('image/png');
                const pdf = new Jspdf('landscape', 'px', 'letter');
                const width = pdf.internal.pageSize.getWidth();
                const height = pdf.internal.pageSize.getHeight();
                const scale = Math.min(
                  width / canvas.width,
                  height / canvas.height
                );

                pdf.addImage(
                  imgData,
                  'JPEG',
                  0,
                  0,
                  canvas.width * scale,
                  canvas.height * scale
                );
                pdf.save('roadmap.pdf');
                setIsExporting(false);
              }}
            >
              PDF
            </MenuItem>
          </Menu>
        </div>
      </div>
      <div className='roadmap-scroll'>
        <div
          ref={roadmapDivRef}
          className='roadmap-container'
          style={{ width: roadmapWidth }}
        >
          <div className='timeline' style={{ width: roadmapWidth }}>
            <div className='epic-name'>Workstream</div>
            {months.map(date => {
              const days = getDaysInMonth(date);
              return (
                <div
                  className='month'
                  key={date.toString()}
                  style={{ minWidth: days * dayWidth }}
                >
                  {format(date, 'MMMM')}
                </div>
              );
            })}
          </div>
          {epics?.map(epic => {
            let lastEvent = { startDay: 0, days: 0 };
            return (
              <div
                className='epic'
                key={epic.key}
                style={{ width: roadmapWidth }}
              >
                <div className='epic-name'>{epic.summary}</div>
                <div
                  className='requirements-container'
                  style={{
                    backgroundPosition: getGridPosition(weekendOffset)
                  }}
                >
                  {epic.requirements?.map((requirement, index) => {
                    let startDate = parse(
                      requirement.startDate,
                      'yyyy-MM-dd',
                      new Date()
                    );
                    let endDate = parse(
                      requirement.endDate,
                      'yyyy-MM-dd',
                      new Date()
                    );
                    if (isMonday(startDate)) {
                      startDate = addDays(startDate, -1); // fill weekend gaps
                    }
                    if (isFriday(endDate)) {
                      endDate = addDays(endDate, 1); // fill weekend gaps
                    }
                    // add a day because end date time is 00:00:00
                    const startDay =
                      getDayOfYear(startDate) - getDayOfYear(startTimeline);
                    const days = differenceInDays(endDate, startDate) + 1;
                    const isNextRow =
                      lastEvent &&
                      lastEvent.startDay + lastEvent.days > startDay;
                    const startDayDiff =
                      !lastEvent || isNextRow
                        ? startDay // next row so start from 0 days
                        : startDay - (lastEvent.startDay + lastEvent.days);
                    lastEvent = { startDay, days };
                    return (
                      <Fragment key={requirement.key}>
                        {isNextRow && (
                          <>
                            <div className='end' />
                            <div className='new-line' />
                          </>
                        )}
                        {startDayDiff > 0 && (
                          <div
                            className='gap'
                            style={{ width: startDayDiff * dayWidth }}
                          />
                        )}
                        <div
                          className='block requirement'
                          style={{
                            width: days * dayWidth,
                            border: `1px solid ${requirement.color}`,
                            color: requirement.color,
                            background: `${adjustColor(
                              requirement.color,
                              160
                            )}9B` // 9B is 60% opacity
                          }}
                        >
                          <span className='requirement-name'>
                            {requirement.summary}
                          </span>
                        </div>
                      </Fragment>
                    );
                  })}
                  <div className='end' />
                </div>
              </div>
            );
          })}
          {showTodayMarker && (
            <div
              className='today-marker'
              style={{ left: workstreamColumnWidth + today * dayWidth }}
            />
          )}
        </div>
      </div>
    </StyledRoadmap>
  );
};

export default QualityRoadmapPage;
